import Image from '@/components/Image';
import MagicJump, { type JumpTarget } from '@/components/MagicJump';
import { env } from '@/utils/env';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useEffect, useRef, type MouseEvent } from 'react';
import { useInView } from 'react-intersection-observer';
import type { StyleHotProductBannerModuleData } from '../types';

type BannerClickArea = '图片' | '按钮';

interface BannerData {
  alt?: string;
  title?: string;
  subtitle?: string;
  title_color?: string;
  button_text?: string;
  button_text_color?: string;
  button_bg_color?: string;
  pc_banner_image?: string;
  wap_banner_image?: string;
  click_effect?: 'jump' | 'style_test';
  jump?: JumpTarget;
}

interface BannerProps {
  deviceType: EPlatform;
  data?: StyleHotProductBannerModuleData | null;
  onOpenStyleQuiz?: () => void;
  onTrackAdExposure?: (jump?: JumpTarget) => void;
  onTrackAdClick?: (clickArea: BannerClickArea, extraProperties: Record<string, string>) => void;
}

function resolveAssetUrl(path?: string): string {
  if (!path) {
    return '';
  }

  if (/^(https?:)?\/\//.test(path) || path.startsWith('blob:')) {
    return path;
  }

  if (path.startsWith('/admin/')) {
    return `${env.NEXT_PUBLIC_S3_URL}${path}`;
  }

  return path;
}

function BannerButton({
  data,
  onOpenStyleQuiz,
  isWeb,
}: {
  data: BannerData;
  onOpenStyleQuiz?: () => void;
  isWeb: boolean;
}) {
  if (!data.button_text) {
    return null;
  }

  const buttonClassName = isWeb
    ? 'inline-flex h-[48px] max-w-full cursor-pointer items-center justify-center overflow-hidden rounded-[4px] border-0 px-[46px] py-[10px] font-bb text-[20px] leading-[24px]'
    : 'flex h-[36px] min-w-[128px] max-w-full cursor-pointer items-center justify-center overflow-hidden rounded-[18px] border-0 px-[32px] py-[8px] font-bb text-[14px] leading-[20px]';
  const buttonStyle = {
    backgroundColor: data.button_bg_color || '#ffffff',
  };
  const buttonTextStyle = {
    color: data.button_text_color || (isWeb ? '#282828' : '#1a1c1e'),
  };

  if (data.click_effect === 'jump') {
    return (
      <MagicJump
        item={data.jump}
        className={buttonClassName}
        style={buttonStyle}
        aria-label={data.button_text}
        data-banner-click-area="button"
      >
        <span className="block min-w-0 max-w-full truncate" style={buttonTextStyle}>
          {data.button_text}
        </span>
      </MagicJump>
    );
  }

  return (
    <button
      type="button"
      className={buttonClassName}
      style={buttonStyle}
      aria-label={data.button_text}
      onClick={onOpenStyleQuiz}
      data-banner-click-area="button"
    >
      <span className="block min-w-0 max-w-full truncate" style={buttonTextStyle}>
        {data.button_text}
      </span>
    </button>
  );
}

function getBannerTrackProperties(data: BannerData) {
  return {
    pits_jump_type: data.jump?.jump_type == null ? '' : `${data.jump.jump_type}`,
    pits_jump_content: data.jump?.Jump_target == null ? '' : `${data.jump.Jump_target}`,
  };
}

export default function Banner({
  deviceType,
  data,
  onOpenStyleQuiz,
  onTrackAdExposure,
  onTrackAdClick,
}: BannerProps) {
  const isWeb = deviceType === EPlatform.WEB;
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.5 });
  const hasTrackedExposureRef = useRef(false);
  const bannerData: BannerData = {
    alt: data?.alt,
    title: data?.title,
    subtitle: data?.subtitle,
    title_color: data?.title_color,
    button_text: data?.button_text,
    button_text_color: data?.button_text_color,
    button_bg_color: data?.button_bg_color,
    pc_banner_image: data?.pc_banner_image,
    wap_banner_image: data?.wap_banner_image,
    click_effect: data?.click_effect,
    jump: data?.jump,
  };
  const rawImageSrc = isWeb
    ? bannerData.pc_banner_image || bannerData.wap_banner_image
    : bannerData.wap_banner_image || bannerData.pc_banner_image;
  const imageSrc = resolveAssetUrl(rawImageSrc);

  const handleBannerClick = (event: MouseEvent<HTMLElement>) => {
    const target = event.target;
    const isButtonClick =
      target instanceof Element && target.closest('[data-banner-click-area="button"]');

    onTrackAdClick?.(isButtonClick ? '按钮' : '图片', getBannerTrackProperties(bannerData));
  };

  useEffect(() => {
    if (!imageSrc || !inView || hasTrackedExposureRef.current) {
      return;
    }

    hasTrackedExposureRef.current = true;
    onTrackAdExposure?.(bannerData.jump);
  }, [bannerData.jump, imageSrc, inView, onTrackAdExposure]);

  if (!imageSrc) {
    return null;
  }

  if (!isWeb) {
    return (
      <section
        ref={ref}
        className="relative w-full overflow-hidden bg-black"
        onClickCapture={handleBannerClick}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={imageSrc}
          alt={bannerData.alt || bannerData.title || 'activity banner'}
          className="block h-auto w-full"
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.19) 69.067%, rgba(0, 0, 0, 0.42) 100%)',
          }}
        />

        <div className="absolute bottom-[24px] left-1/2 flex w-[295px] max-w-[calc(100%-80px)] -translate-x-1/2 flex-col items-center gap-[12px]">
          <div
            className="flex w-full flex-col items-center gap-[4px] text-center"
            style={{ color: bannerData.title_color || '#ffffff' }}
          >
            {bannerData.title && (
              <h1 className="w-[calc(100vw-24px)] max-w-[calc(100vw-24px)] truncate font-bb text-[24px] leading-[29px] font-bold not-italic">
                {bannerData.title}
              </h1>
            )}
            {bannerData.subtitle && (
              <p className="line-clamp-2 w-full font-br text-[12px] leading-[14px]">
                {bannerData.subtitle}
              </p>
            )}
          </div>
          <BannerButton data={bannerData} isWeb={isWeb} onOpenStyleQuiz={onOpenStyleQuiz} />
        </div>
      </section>
    );
  }

  return (
    <section
      ref={ref}
      className="relative w-full overflow-hidden bg-black"
      style={{ aspectRatio: '1920 / 800' }}
      onClickCapture={handleBannerClick}
    >
      <div className="absolute left-[-3.75%] top-0 h-[112.125%] w-[122.24%] overflow-hidden">
        <Image
          src={imageSrc}
          alt={bannerData.alt || bannerData.title || 'activity banner'}
          fill
          priority
          sizes="122vw"
          className="block h-full w-full object-cover object-center"
        />
      </div>
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'linear-gradient(180deg, rgba(0, 0, 0, 0.1) 54.693%, rgba(0, 0, 0, 0.48) 100%)',
        }}
      />

      <div className="absolute bottom-[60px] left-1/2 flex w-[668px] max-w-[calc(100%-96px)] -translate-x-1/2 flex-col items-center gap-[32px]">
        <div
          className="flex w-full flex-col items-center gap-[8px] text-center"
          style={{ color: bannerData.title_color || '#ffffff' }}
        >
          {bannerData.title && (
            <h1 className="w-full truncate font-bb text-[48px] leading-normal">
              {bannerData.title}
            </h1>
          )}
          {bannerData.subtitle && (
            <p className="line-clamp-2 w-full font-br text-[16px] font-normal leading-normal not-italic">
              {bannerData.subtitle}
            </p>
          )}
        </div>
        <BannerButton data={bannerData} isWeb={isWeb} onOpenStyleQuiz={onOpenStyleQuiz} />
      </div>
    </section>
  );
}
